import { IconButton, TextButton } from '@creditornot/cb-button';
import { ArrowLeft, CaretDown, CaretUp, MagnifyingGlass } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Spinner } from '@creditornot/cb-progress';
import { Radio } from '@creditornot/cb-radio';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import { usePermissions } from 'common/wolt-permissions';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';
import { languages } from 'config';
import PageLoadError from 'core/PageLoadError';
import { updateSharedMenuInfo } from 'modules/shared-menu/api';
import { HeaderContainer } from 'modules/shared-menu/components/CommonStyles';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { EditSharedMenuInfoPayload } from 'modules/shared-menu/types';

import AddLanguage from './components/AddLanguage';
import LanguageComparisonSelector from './components/LanguageComparisonSelector';
import Sections from './components/Sections';
import TranslationStats from './components/TranslationStats';
import VerifyTranslationNotice from './components/VerifyTranslationNotice';
import useRequestQueue from './hooks/useTranslationQueue';
import { LanguageStats, TranslationLabelItem, TranslationSection, TranslationSectionItem } from './types';
import * as utils from './utils';

const Header = styled(T.Heading4)`
  display: flex;
  flex-direction: column;
`;

const MessageHeading = styled(T.Heading6)`
  margin-block-end: 12px;
`;

const StyledTextButton = styled(TextButton)`
  font-weight: bold;
  margin-block-end: 8px;
`;

const TranslationStatsHeading = styled(T.Heading6)`
  margin-block-end: 12px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const SectionsHeading = styled(T.Heading6)`
  margin-inline-end: 48px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const StyledDisplayModeSelector = styled.div`
  display: flex;
  align-items: center;
`;

const DisplayModeLabel = styled.label`
  display: flex;
  align-items: center;
  & + & {
    margin-inline-start: 24px;
  }
`;

const DisplayModeSelector = ({
  filterUntranslated,
  setFilterUntranslated,
}: {
  filterUntranslated: boolean;
  setFilterUntranslated: (filterUntranslated: boolean) => void;
}) => {
  const intl = useIntl();
  return (
    <StyledDisplayModeSelector>
      <DisplayModeLabel>
        <Radio
          name="displayMode"
          checked={!filterUntranslated}
          onChange={() => {
            setFilterUntranslated(false);
          }}
        >
          {intl.formatMessage({ id: 'translation.showEverything' })}
        </Radio>
      </DisplayModeLabel>
      <DisplayModeLabel>
        <Radio
          name="displayMode"
          checked={filterUntranslated}
          onChange={() => {
            setFilterUntranslated(true);
          }}
        >
          {intl.formatMessage({ id: 'translation.showUntranslated' })}
        </Radio>
      </DisplayModeLabel>
    </StyledDisplayModeSelector>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${cbSemanticCssVars.surfaceMain};
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

const HeaderWrapper = styled.div`
  background: ${cbSemanticCssVars.surfaceMain};
  width: 100%;
`;

const LanguagesHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledVerifyTranslationNotice = styled(VerifyTranslationNotice)`
  margin-block-start: 24px;
`;

const Section = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 24px;
`;

const SectionWithDivider = styled(Section)`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledTranslationSections = styled(Sections)`
  flex: 1;
  width: 100%;
  margin: 0 auto;
`;

const BodyHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const BodyHeaderLeftContent = styled.div`
  display: flex;
`;

const SearchInput = styled(Input)`
  width: 240px;
`;

const searchInSections = (search: string, sections: TranslationSection[]) => {
  const normalizedSearch = search.toLocaleLowerCase();
  return sections.reduce<TranslationSection[]>((accSections, currSection) => {
    const filteredSubsections = currSection.subsections.filter(sub =>
      sub.items.some(item => item.translations.some(t => t.value.toLocaleLowerCase().includes(normalizedSearch))),
    );
    if (filteredSubsections.length === 0) {
      return accSections;
    }
    return [...accSections, { label: currSection.label, subsections: filteredSubsections }];
  }, []);
};

export const TranslationsRoot = () => {
  const intl = useIntl();
  const history = useHistory();
  const { selection, loadingInitialSelectionData, selectionDataError, offers, options, refetchSelectionData } =
    useSelectionData();

  const { type } = useScopeContext();

  const { menuEditorPage } = useListingManagerRoutes();

  const sections = selection && selection.categories && utils.buildSections(selection, offers, options);
  const primaryLanguage = selection.primary_language;

  const [languagesSectionOpen, setLanguagesSectionOpen] = useState(false);
  const [leftLanguage, setLeftLanguage] = useState<Language | null>(null);
  const [rightLanguage, setRightLanguage] = useState<Language | null>(null);
  // NOTE: Newly added languages are not removed from this list even when a translation for such language is added
  const [addedLanguages, setAddedLanguages] = useState<Language[]>([]);
  const [filterUntranslated, setFilterUntranslated] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  const fullSelection = { selection, offers, options };

  const { addTranslationRequest, pendingTasks, erroredTasks } = useRequestQueue({
    selectionData: fullSelection,
    rightLanguage,
    leftLanguage,
  });

  const addLanguage = (language: Language) => {
    setLeftLanguage(rightLanguage ? leftLanguage || language : leftLanguage || primaryLanguage);
    setRightLanguage(rightLanguage || language);
    setAddedLanguages([...addedLanguages, language]);
  };

  const permissions = usePermissions();
  const hasPermissionToChangeMenuLanguage = permissionUtils.hasMenuLanguageChangePermission(permissions);

  const { mutate: executeUpdateSelectionInfo, isLoading: isUpdatingSelectionInfo } = useMutation(
    async (language: Language) => {
      const { scope, version, id, categories, linked_venue_ids, draft, ...payloadShapedSelection } = selection;
      const updateSelectionInfoPayload: EditSharedMenuInfoPayload = {
        ...payloadShapedSelection,
        // TODO: check cases for names in other languages. Is that a real scenario?
        name: [{ value: selection.name[0].value, lang: language, verified: true }],
        categoryIds: categories.map(({ id }) => id),
        primary_language: language,
      };

      return updateSharedMenuInfo(selection.id, updateSelectionInfoPayload);
    },
    {
      onSuccess: () => {
        refetchSelectionData();
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'errors.unknown-error-retry-contact' }), { duration: Infinity });
      },
    },
  );

  let content;
  if (loadingInitialSelectionData) {
    content = null;
  } else if (selectionDataError) {
    content = <PageLoadError />;
  } else if (!primaryLanguage) {
    content = (
      <HeaderWrapper>
        <Section>
          <MessageHeading>
            <FormattedMessage id="translation.translationTool" />
          </MessageHeading>
          <T.Body1>
            <FormattedMessage id="translation.primaryLanguageMissing" />
          </T.Body1>
        </Section>
      </HeaderWrapper>
    );
  } else if (sections && sections.length === 0) {
    content = (
      <HeaderWrapper>
        <Section>
          <MessageHeading>
            <FormattedMessage id="translation.translationTool" />
          </MessageHeading>
          <T.Body1>
            <FormattedMessage id="translation.noTranslatableMenuContent" />
          </T.Body1>
        </Section>
      </HeaderWrapper>
    );
  } else if (sections) {
    const statsByLanguage = new Map<Language, LanguageStats>();

    sections.forEach(section => {
      section.subsections.forEach(subsection => {
        subsection.items.forEach(item => {
          item.translations.forEach(t => {
            if (t.value) {
              const stats = {
                language: t.lang,
                translated: 0,
                unverified: 0,
                ...statsByLanguage.get(t.lang),
              };
              stats.translated += 1;
              if (!t.verified) {
                stats.unverified += 1;
              }
              statsByLanguage.set(t.lang, stats);
            }
          });
        });
      });
    });

    addedLanguages.forEach(language => {
      if (!statsByLanguage.get(language)) {
        statsByLanguage.set(language, {
          language,
          translated: 0,
          unverified: 0,
        });
      }
    });

    const availableLanguages = [...statsByLanguage.keys()];

    const visibleSections = filterUntranslated
      ? utils.filterUntranslated(
          [leftLanguage, rightLanguage].filter((l): l is Language => !!l && l !== primaryLanguage),
          sections,
        )
      : sections;

    const visibleSectionsWithSearch = searchFilter ? searchInSections(searchFilter, visibleSections) : visibleSections;

    const flatVisibleSections = visibleSectionsWithSearch.reduce<(TranslationLabelItem | TranslationSectionItem)[]>(
      (acc, curr) => {
        const subsection = curr.subsections.flatMap(subsection =>
          subsection.label
            ? [
                { itemType: 'subsection' as const, label: subsection.label, price: subsection.price },
                ...subsection.items,
              ]
            : subsection.items,
        );
        return [...acc, { itemType: 'section' as const, label: curr.label }, ...subsection];
      },
      [],
    );

    content = (
      <>
        <HeaderWrapper>
          <SectionWithDivider>
            <LanguagesHeader>
              <TranslationStatsHeading>
                <FormattedMessage id="translation.languages" />
              </TranslationStatsHeading>
              <IconButton variant="neutral" size="small" onClick={() => setLanguagesSectionOpen(prev => !prev)}>
                {languagesSectionOpen ? <CaretUp /> : <CaretDown />}
              </IconButton>
            </LanguagesHeader>
            {languagesSectionOpen && (
              <>
                <TranslationStats
                  hasPermissionToChangeMenuLanguage={hasPermissionToChangeMenuLanguage}
                  stats={[...statsByLanguage.values()]}
                  primaryLanguage={primaryLanguage}
                  pendingPrimaryLanguage={isUpdatingSelectionInfo}
                  onSetPrimaryLanguage={executeUpdateSelectionInfo}
                  intl={intl}
                />
                <AddLanguage languages={languages.filter(l => !availableLanguages.includes(l))} onAdd={addLanguage} />
              </>
            )}
          </SectionWithDivider>
        </HeaderWrapper>
        <Section>
          <BodyHeader>
            <BodyHeaderLeftContent>
              <SectionsHeading>
                <FormattedMessage id="translation.translationTool" />
              </SectionsHeading>
              <DisplayModeSelector
                filterUntranslated={filterUntranslated}
                setFilterUntranslated={setFilterUntranslated}
              />
            </BodyHeaderLeftContent>
            <SearchInput
              stretch={false}
              onClearClick={() => setSearchFilter('')}
              placeholder="Search..."
              icon={<MagnifyingGlass />}
              disabled={!leftLanguage && !rightLanguage}
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
            />
          </BodyHeader>
          <LanguageComparisonSelector
            availableLanguages={availableLanguages}
            leftLanguage={leftLanguage}
            rightLanguage={rightLanguage}
            onLeftLanguageChange={setLeftLanguage}
            onRightLanguageChange={setRightLanguage}
          />
          <StyledVerifyTranslationNotice
            primaryLanguage={primaryLanguage}
            statsByLanguage={statsByLanguage}
            leftLanguage={leftLanguage}
            rightLanguage={rightLanguage}
          />
        </Section>
        <StyledTranslationSections
          fullSelection={fullSelection}
          leftLanguage={leftLanguage}
          rightLanguage={rightLanguage}
          sections={flatVisibleSections}
          primaryLanguage={primaryLanguage}
          erroredTasks={erroredTasks}
          pendingTasks={pendingTasks}
          onSaveTranslation={addTranslationRequest}
        />
      </>
    );
  } else {
    return <PageLoadError />;
  }

  if (loadingInitialSelectionData) {
    return (
      <SpinnerContainer>
        <Spinner size="large" />
      </SpinnerContainer>
    );
  }

  return (
    <Root>
      {type !== 'venue' && (
        <HeaderContainer>
          <Header>
            <StyledTextButton
              variant="neutral"
              size="small"
              icon={<ArrowLeft />}
              onClick={() => history.push(menuEditorPage())}
            >
              <FormattedMessage id="shared-menu.editor.back-button" />
            </StyledTextButton>
            <FormattedMessage id="navigation.translation" />
          </Header>
        </HeaderContainer>
      )}
      {content}
    </Root>
  );
};
